import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Avatar, Badge, Button } from "@realtyflows/ui";
import {
  getAuditQueue,
  getContractFolder,
  getFolderCompliance,
  updateComplianceItem,
  brokerApproveFolder,
  brokerRequestChanges,
} from "../api";
import FolderStatusBadge from "../components/FolderStatusBadge";
import HealthScore from "../components/HealthScore";

/**
 * BrokerAudit — V3 Features 3.2 + 3.5
 *
 * Step-through audit experience: the broker reviews one folder at a
 * time from the team's audit queue (folders in pending_review, plus
 * folders with missing required docs).
 *
 * Per-document approval (3.5) is handled inline via the existing
 * compliance status workflow ('uploaded' → 'approved').
 *
 * Final actions per folder:
 *   - Approve + Close
 *   - Approve (keep open)
 *   - Request changes (with comments)
 *   - Skip to next
 */
export default function BrokerAudit({ user }) {
  const navigate = useNavigate();
  const teamId = user?.team_id;

  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [folder, setFolder] = useState(null);
  const [compliance, setCompliance] = useState({ items: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [changesComment, setChangesComment] = useState("");

  // Load the queue once
  useEffect(() => {
    if (!teamId) { setLoading(false); return; }
    getAuditQueue(teamId)
      .then((q) => { setQueue(q || []); })
      .catch(() => setQueue([]))
      .finally(() => setLoading(false));
  }, [teamId]);

  // Load the current folder when the index changes
  const currentId = queue[index]?.id;
  useEffect(() => {
    if (!currentId) { setFolder(null); setCompliance({ items: [], summary: null }); return; }
    Promise.all([getContractFolder(currentId), getFolderCompliance(currentId)])
      .then(([f, c]) => { setFolder(f); setCompliance(c); })
      .catch(() => { setFolder(null); });
  }, [currentId]);

  const advance = () => {
    if (index >= queue.length - 1) {
      setIndex(queue.length);  // off the end → "all done" state
    } else {
      setIndex(index + 1);
    }
  };

  const handleApproveItem = async (item, status) => {
    setActionBusy(true);
    try {
      await updateComplianceItem(currentId, item.id, { status });
      const c = await getFolderCompliance(currentId);
      setCompliance(c);
    } catch (err) {
      window.alert(err.message || "Could not update item");
    } finally {
      setActionBusy(false);
    }
  };

  const handleApprove = async (closeAfter) => {
    setActionBusy(true);
    try {
      await brokerApproveFolder(currentId, { closeAfter });
      advance();
    } catch (err) {
      window.alert(err.message || "Could not approve");
    } finally {
      setActionBusy(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!changesComment.trim()) {
      window.alert("Please describe what needs to change");
      return;
    }
    setActionBusy(true);
    try {
      await brokerRequestChanges(currentId, changesComment.trim());
      setShowChangesModal(false);
      setChangesComment("");
      advance();
    } catch (err) {
      window.alert(err.message || "Could not request changes");
    } finally {
      setActionBusy(false);
    }
  };

  if (!teamId) {
    return (
      <Panel padding={28} style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
          Quick Audit
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body }}>
          Quick Audit is available to team owners only.
        </div>
      </Panel>
    );
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading audit queue...</div>;
  }

  if (queue.length === 0 || index >= queue.length) {
    return (
      <Panel padding={32} style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 14 }}>🎉</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
          {queue.length === 0 ? "Inbox zero" : "All caught up"}
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 20, fontFamily: font.body }}>
          {queue.length === 0
            ? "Nothing needs broker review right now."
            : `You reviewed ${queue.length} folder${queue.length === 1 ? "" : "s"} in this session.`}
        </div>
        <Button variant="primary" onClick={() => navigate("/broker")}>Back to dashboard</Button>
      </Panel>
    );
  }

  const current = queue[index];

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 900, margin: "0 auto" }}>
      {/* Progress bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button
          onClick={() => navigate("/broker")}
          style={{ background: "none", border: "none", fontSize: 12, color: t.textMuted, cursor: "pointer", fontFamily: font.body }}
        >
          ← Back to broker dashboard
        </button>
        <span style={{ fontSize: 12, color: t.textMuted, fontFamily: font.mono }}>
          Folder {index + 1} of {queue.length}
        </span>
      </div>
      <div style={{ height: 4, background: t.border, borderRadius: 99, marginBottom: 20, overflow: "hidden" }}>
        <div style={{
          width: `${((index) / queue.length) * 100}%`, height: "100%",
          background: t.emerald, transition: "width 0.3s ease",
        }} />
      </div>

      {folder ? (
        <>
          {/* Header */}
          <Panel padding={20} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display, marginBottom: 4 }}>
                  {folder.name}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
                  <Avatar src={current.agentPhoto} name={current.agentName} size={20} />
                  <span>Owned by {current.agentName}</span>
                  {current.clientName && <span>· Client: {current.clientName}</span>}
                </div>
                {(folder.property_address || current.propertyAddress) && (
                  <div style={{ fontSize: 12, color: t.textSecondary, marginTop: 6, fontFamily: font.body }}>
                    {folder.property_address || current.propertyAddress}
                  </div>
                )}
              </div>
              <FolderStatusBadge status={folder.status} />
            </div>
          </Panel>

          {/* Health + per-item review */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16, marginBottom: 16 }}>
            <Panel padding={16}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 10, fontFamily: font.display }}>
                Health
              </div>
              <HealthScore folderId={currentId} />
            </Panel>

            <Panel padding={16}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
                  Compliance Items
                </span>
                {compliance.summary && (
                  <Badge color={compliance.summary.percentComplete === 100 ? t.emerald : compliance.summary.percentComplete >= 60 ? t.amber : t.coral}>
                    {compliance.summary.requiredSatisfied}/{compliance.summary.requiredTotal} required
                  </Badge>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
                {compliance.items.map((item) => {
                  const isMissing = item.status === "missing";
                  const isApproved = item.status === "approved";
                  return (
                    <div key={item.id} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: 8,
                      background: isApproved ? t.emerald + "08" : isMissing ? t.coral + "08" : t.surfaceWarm,
                      border: `1px solid ${isMissing ? t.coral + "30" : t.border}`,
                      borderRadius: 6,
                    }}>
                      <div style={{ flex: 1, fontSize: 11, color: t.textPrimary, fontFamily: font.body, fontWeight: 600 }}>
                        {item.name}
                        {!item.required && <span style={{ fontSize: 9, color: t.textMuted, marginLeft: 4 }}>(optional)</span>}
                      </div>
                      <Badge color={isApproved ? t.emerald : isMissing ? t.coral : t.sky}>{item.status}</Badge>
                      {item.status === "uploaded" && (
                        <Button variant="primary" size="sm" disabled={actionBusy} onClick={() => handleApproveItem(item, "approved")}>
                          ✓ Approve
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>

          {/* Action bar */}
          <Panel padding={16}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button variant="primary" disabled={actionBusy} onClick={() => handleApprove(true)}>
                ✓ Approve + Close folder
              </Button>
              <Button variant="outline" disabled={actionBusy} onClick={() => handleApprove(false)}>
                Approve (keep open)
              </Button>
              <Button variant="outline" disabled={actionBusy} onClick={() => setShowChangesModal(true)}>
                Request changes
              </Button>
              <div style={{ flex: 1 }} />
              <Button variant="outline" disabled={actionBusy} onClick={advance}>
                Skip →
              </Button>
              <Button variant="outline" disabled={actionBusy} onClick={() => navigate(`/contracts/folders/${currentId}`)}>
                Open full folder
              </Button>
            </div>
          </Panel>
        </>
      ) : (
        <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading folder...</div>
      )}

      {/* Request changes modal */}
      {showChangesModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div style={{
            background: t.surface, borderRadius: 16, padding: 24,
            maxWidth: 520, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
              Request changes from {current.agentName}
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, fontFamily: font.body }}>
              Folder will move to "Active" and the agent will be notified with your comments.
            </div>
            <textarea
              value={changesComment}
              onChange={(e) => setChangesComment(e.target.value)}
              placeholder="What needs to change?"
              rows={5}
              style={{
                width: "100%", padding: 12, fontSize: 13, fontFamily: font.body,
                border: `1.5px solid ${t.border}`, borderRadius: 8,
                background: t.surfaceWarm, color: t.textPrimary, outline: "none", resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <Button variant="outline" onClick={() => setShowChangesModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleRequestChanges} disabled={actionBusy || !changesComment.trim()}>
                Send back to agent
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
